import { useEffect } from 'react'

import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useUpdateAgent from '~/features/agents/hooks/useUpdateAgent'
import { getAgentChatSettings } from '~/features/agents/utils'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { defaultChatBotLayout } from '~/features/settings/components/chatBot/utils'
import { sessionStore } from '~/store'

const useGetOrPostChatbotSettings = () => {
  const agentId = sessionStore((state) => state.agent.id) || ''

  const { partner } = usePartnerCentralized()
  const { selectedAgent: agent, isSuccess } = useGetAgentCentralized()
  const isPartner = useIsPartner()

  const { chatSettings } = agent
  // updateAgent's own ChatSetting write upserts (see agent-service's
  // agentService.ts), so it's safe to call unconditionally here even for an
  // agent with no ChatSetting row yet — no separate "create" endpoint needed.
  const { data: updatedAgentResponse, mutate: updateAgentMutation } =
    useUpdateAgent(agentId)

  const { layout } = getAgentChatSettings(chatSettings) || {}

  const defaultLayout =
    isPartner && partner.chatTemplateColors
      ? partner.chatTemplateColors
      : defaultChatBotLayout

  useEffect(() => {
    if (isSuccess && !chatSettings) {
      updateAgentMutation({
        agentId,
        payload: { chatSettings: { layout: defaultLayout } },
        // Silent on both success AND failure — this is a background "ensure
        // default settings exist" effect, not a user-initiated save.
        // useCreateChatBotSettings had no onError handler at all, so a
        // failure here was previously invisible; useUpdateAgent shows a
        // visible error toast by default, which hideErrorToast suppresses
        // to match that original silent-failure behavior.
        hideToast: true,
        hideErrorToast: true,
        // skipInvalidation — useUpdateAgent's onSuccess otherwise does an
        // IMMEDIATE, synchronous queryClient.setQueryData on the exact cache
        // key chatBotSettingsForm's selectedAgent derives from. That form
        // uses enableReinitialize: true, so if this background effect
        // resolves while someone is actively editing the settings form, its
        // in-progress (unsaved) field values get silently discarded and
        // replaced by the just-created defaults — indistinguishable from
        // "my changes vanished" (Cursor-flagged, Medium severity).
        // useCreateChatBotSettings never had this risk — it only called
        // invalidateQueries (marks stale, refetches on its own schedule),
        // never an immediate cache push. Skipping invalidation here doesn't
        // lose anything: this hook already reads the freshly-created layout
        // straight off the mutation's own response (updatedAgentResponse
        // below), and every other view of this agent picks up the change on
        // its own next natural refetch regardless.
        skipInvalidation: true,
      })
    }
  }, [isSuccess, chatSettings, updateAgentMutation, agentId, defaultLayout])

  if (chatSettings && layout) {
    return layout
  }

  return getAgentChatSettings(updatedAgentResponse?.data?.chatSettings)?.layout
}

export default useGetOrPostChatbotSettings
